/**
 * motion-base.js — drives the entrance reveal via IntersectionObserver.
 *
 * Loaded once globally (theme.liquid). When any element with
 * `data-motion-entrance` enters the viewport it gets `data-motion-revealed="true"`,
 * triggering the transition from hidden → revealed state defined in motion-base.css.
 *
 * Respects `prefers-reduced-motion: reduce` — reveals immediately, no transition.
 */

(function () {
  'use strict';

  // Single shared observer; re-used for new nodes as Shopify's editor or
  // SPAs swap section markup without a full page reload. Without
  // re-observing, freshly-mounted [data-motion-entrance] elements stay
  // at opacity:0 forever — looks like the text "disappears" when a
  // merchant edits a setting in the theme customizer.
  var observer = null;
  var prefersReduced = false;

  function reveal(el) {
    el.setAttribute('data-motion-revealed', 'true');
  }

  function observeNode(el) {
    if (el.getAttribute('data-motion-revealed') === 'true') return;
    if (!observer) { reveal(el); return; }
    observer.observe(el);
  }

  function rescan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-motion-entrance]').forEach(function (el) {
      if (prefersReduced) { reveal(el); return; }
      observeNode(el);
    });
  }

  function init() {
    prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    if (!prefersReduced && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });
    }

    rescan();

    // Shopify theme editor lifecycle: when a merchant edits a section's
    // settings, Shopify replaces that section's DOM and fires these events.
    // Re-scan to attach the observer to freshly-mounted nodes.
    document.addEventListener('shopify:section:load', function (e) { rescan(e.target); });
    document.addEventListener('shopify:section:reorder', function () { rescan(); });
    document.addEventListener('shopify:block:select', function (e) { rescan(e.target); });

    // Belt-and-suspenders: any new [data-motion-entrance] node added by
    // anything else (SPA swaps, custom AJAX) gets picked up too.
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.matches && n.matches('[data-motion-entrance]')) observeNode(n);
            if (n.querySelectorAll) rescan(n);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
